import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Role } from 'context/authentication/Role.types'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { hasAccess } from 'global/helpers/authorization'
import useDialog from 'global/hooks/useDialog'
import { Fragment, type ReactElement, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import DocumentLink from 'screens/StatusManager/DocumentLink'
import DocumentsUploadDialog from 'screens/StatusManager/DocumentsUploadDialog'
import StatusUpdateWithAttachmentsDialog from 'screens/StatusManager/StatusUpdateWithAttachmentsDialog'
import StopStatusChip from 'screens/StatusManager/StopStatusChip'
import LocationLink from 'screens/TripDashboard/LocationLink'
import AssetIdAttribute from 'screens/TripDetails/AssetIdAttribute'
import StatusHistory from 'screens/TripDetails/StatusHistory'
import type { PrimaryStopProps } from './StopsView.types'
import LegCargo from 'screens/TripDetails/LegCargo'
import LegHeader from 'screens/TripDetails/LegHeader'
import PrimaryActions from 'screens/TripDetails/PrimaryActions'
import StatusDateMessage from 'screens/TripDetails/StatusDateMessage'
import StopType from 'screens/TripDetails/StopType'
import type { KNDataCardAction } from 'screens/TripDetails/TripDetails.types'
import UitReferenceAttribute from 'screens/TripDetails/UitReferenceAttribute'

const PrimaryStop = ({
  trip,
  leg,
  stop,
  weblinkToken,
}: PrimaryStopProps): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const [tripDetailsState] = useContext(TripDetailsContext)
  const deliveryStop = stop.type === 'PUP' ? leg.wayPoints.find((stop) => stop.type === 'DEL') : undefined
  const stopErrors = tripDetailsState.stopsErrors?.filter((error) => error['waypointCid'] === stop.wayPointCid)
  const regularStatusUpdateDialog = useDialog<typeof StatusUpdateWithAttachmentsDialog>()
  const discrepancyStatusUpdateDialog = useDialog<typeof StatusUpdateWithAttachmentsDialog>()
  const documentsUploadDialog = useDialog<typeof DocumentsUploadDialog>()

  const actions: KNDataCardAction[] = []
  if (stop.availableRegularStatuses.length === 1) {
    actions.push({
      label: t(`waypoints.statuses.${stop.availableRegularStatuses[0]!.id}`),
      variant: 'outlined',
      color: 'success',
      handler: regularStatusUpdateDialog.handleOpen,
      disabled: stop.availableRegularStatuses[0]!.bulkStatusUpdateOnly,
    })
  } else if (stop.availableRegularStatuses.length > 1) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.update_status_and_documents'),
      variant: 'outlined',
      color: 'success',
      handler: regularStatusUpdateDialog.handleOpen,
    })
  }
  if (stop.availableDiscrepancyStatuses.length > 0) {
    actions.push({
      icon: <WarningAmberOutlinedIcon />,
      variant: 'outlined',
      color: 'error',
      handler: discrepancyStatusUpdateDialog.handleOpen,
    })
  }
  if (stop.statuses.length > 0 && stop.availableStatuses.length === 0) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.upload_documents'),
      variant: 'outlined',
      handler: documentsUploadDialog.handleOpen,
    })
  }

  return (
    <>
      <Stack spacing={2}>
        {stopErrors.length > 0 && (
          <Alert severity="error">
            {stopErrors.map((error, index) => (
              <Fragment key={error.message}>
                {index > 0 && <br />}
                {error.message}
              </Fragment>
            ))}
          </Alert>
        )}

        <Stack spacing={1}>
          <Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {stop.statuses[0] && (
                <StopStatusChip statusId={stop.statuses[0].id} />
              )}
              <StopType stop={stop} />
            </Stack>
            <LegHeader trip={trip} leg={leg} size="large" />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0, md: 1 }}>
              <AssetIdAttribute leg={leg} stopType={stop.type} />
              <UitReferenceAttribute leg={leg} />
            </Stack>
          </Stack>
          <StatusDateMessage leg={leg} stop={stop} />

          <Stack spacing={0.25}>
            <Typography variant="p6" color="primary.light" component="p">
              {t('screens.cs.trip_dashboard.card.columns.cargo')}
            </Typography>
            <LegCargo leg={leg} />
          </Stack>

          {isMobile && stop.type === 'PUP' && leg.collectionInstructions && (
            <Stack>
              <Typography variant="p6" color="primary.light" component="p">
                {t('screens.cs.trip_details.shipment_details.collection_instructions')}
              </Typography>
              <Typography variant="p3" color="primary.main" component="p">
                {leg.collectionInstructions}
              </Typography>
            </Stack>
          )}

          {isMobile && stop.type === 'DEL' && leg.deliveryInstructions && (
            <Stack>
              <Typography variant="p6" color="primary.light" component="p">
                {t('screens.cs.trip_details.shipment_details.delivery_instructions')}
              </Typography>
              <Typography variant="p3" color="primary.main" component="p">
                {leg.deliveryInstructions}
              </Typography>
            </Stack>
          )}

          {deliveryStop && (
            <Stack>
              <Typography variant="p6" color="primary.light" component="p">
                {t('screens.cs.trip_details.card.delivery_address')}
              </Typography>
              <LocationLink
                countryCode={deliveryStop.address.countryCode}
                city={deliveryStop.address.city.join(', ')}
                zipCode={deliveryStop.address.zipCode}
                street={deliveryStop.address.street.join(', ')}
                marker={deliveryStop.geoPoint ? {
                  id: deliveryStop.wayPointCid,
                  latitude: deliveryStop.geoPoint.latitude,
                  longitude: deliveryStop.geoPoint.longitude,
                  type: deliveryStop.type,
                } : undefined}
                lineBreaks={isMobile ? 'all' : undefined}
              />
            </Stack>
          )}

          {stop.statuses.length > 0 && (
            <Stack>
              <Typography variant="p6" color="primary.light" component="p">
                {stop.type === 'PUP' && t('screens.cs.trip_details.update_status.pickup_status')}
                {stop.type === 'DEL' && t('screens.cs.trip_details.update_status.delivery_status')}
              </Typography>
              <StatusHistory statuses={stop.statuses} withRemarks />
            </Stack>
          )}

          {stop.documents && (
            <Stack>
              <Typography variant="p6" color="primary.light" component="p">
                {t('screens.cs.trip_details.card.attachments')}
              </Typography>
              <Typography variant="p4" color="primary.main" component="p">
                {stop.documents
                  .filter((file) => ['POP', 'POD'].includes(file.fileType))
                  .map((file, index) => (
                    <DocumentLink
                      key={file.id}
                      file={file}
                      index={index > 1 ? index : undefined}
                      weblinkToken={weblinkToken}
                    />
                  ))}
                {stop.documents
                  .filter((file) => !['POP', 'POD'].includes(file.fileType))
                  .map((file) => (
                    <DocumentLink key={file.id} file={file} weblinkToken={weblinkToken} />
                  ))}
              </Typography>
            </Stack>
          )}
        </Stack>

        {actions.length > 0 && (hasAccess(Role.Editor) || weblinkToken) && (
          <PrimaryActions actions={actions} />
        )}
      </Stack>

      {stop.availableRegularStatuses.length > 0 && (
        <StatusUpdateWithAttachmentsDialog
          payload={{
            trip,
            leg,
            stop,
            weblinkToken,
            noDiscrepancyStatuses: true,
          }}
          open={regularStatusUpdateDialog.isOpen}
          onAction={regularStatusUpdateDialog.handleAction}
          onClose={regularStatusUpdateDialog.handleClose}
        />
      )}

      {stop.availableDiscrepancyStatuses.length > 0 && (
        <StatusUpdateWithAttachmentsDialog
          payload={{
            trip,
            leg,
            stop,
            weblinkToken,
            noRegularStatuses: true,
          }}
          open={discrepancyStatusUpdateDialog.isOpen}
          onAction={discrepancyStatusUpdateDialog.handleAction}
          onClose={discrepancyStatusUpdateDialog.handleClose}
        />
      )}

      {stop.statuses.length > 0 && stop.availableStatuses.length === 0 && (
        <DocumentsUploadDialog
          payload={{
            trip,
            leg,
            stop,
            weblinkToken,
          }}
          open={documentsUploadDialog.isOpen}
          onAction={documentsUploadDialog.handleAction}
          onClose={documentsUploadDialog.handleClose}
        />
      )}
    </>
  )
}

export default PrimaryStop
